import type {
  ICommonAPIResponse,
  IFinishGameSessionResponse,
  IGamesListResponse,
  IGenericAPIResponse,
  IGetAllRoomsResponse,
} from '#api';
import type { IDeclineUnityChallengePayload, IGameData } from '#shared/types/games.types';
import type { GameWindowData } from '~/composables/gameLauncher';
import { LazyModalManageFriends } from '#components';
import { EAppCustomHooks } from '#shared/types/custom.hooks.types';
import { EGameCategory } from '#shared/types/games.types';
import { stores } from '~/store/index';

function getFrontendBuyInConfig(source: any) {
  let buyInConfig: Record<string, any>;

  try {
    buyInConfig = JSON.parse(source);
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (error) {
    buyInConfig = null;
  }

  return buyInConfig;
}

function getFrontendBulletDescription(descriptionString: string): IBulletItem[] {
  let bulletDescription: IBulletItem[] = [];

  if (descriptionString?.length) {
    const chunk: string[] = descriptionString.split('\n');

    bulletDescription = chunk.map((item) => {
      const split = item.split('|');
      return { icon: split[0], content: split[1] } as IBulletItem;
    });
  }

  return bulletDescription;
}

interface IPrivateRoom {
  privateRoomId: string;
  privateRoomToken: string;
  bankId: number;
  gameId: number;
  isUnity: boolean;
}

interface IFinishGameSessionParams {
  sid?: string;
  privateRoomId?: string;
}

export function sortPvpGamesLobby(games: IGameData[]) {
  const sortOrder = [
    'Multiplinko2',
    'MultiMines',
    'Escape The Circle',
    'Scarab Plinko',
    '21 Duel',
    'Mobile Warfare',
    'MaXplosion',
    'Roll Or Hold',
    'Rolling 21',
    'Max Slam',
    'Max Blast Champs',
    'Max Blocks',
  ];
  return sortGames(games, sortOrder);
}

export function sortPvpGamesDesktop(games: IGameData[]) {
  const sortOrder = [
    'Multiplinko2',
    'MultiMines',
    'Escape The Circle',
    'Mobile Warfare',
    'MaXplosion',
    'Scarab Plinko',
    'Roll Or Hold',
    'Rolling 21',
    '21 Duel',
    'Max Slam',
    'Max Blocks',
  ];
  return sortGames(games, sortOrder);
}

export function sortPvpGamesMobile(games: IGameData[]) {
  const sortOrder = [
    'Multiplinko2',
    'MultiMines',
    'Escape The Circle',
    'Max Slam',
    'Mobile Warfare',
    'MaXplosion',
    'Scarab Plinko',
    'Roll Or Hold',
    'Rolling 21',
    '21 Duel',
    'Max Blocks',
  ];
  return sortGames(games, sortOrder);
}

export function sortPvpGames(games: IGameData[]) {
  const sortOrder = [
    'Multiplinko2',
    'MultiMines',
    'Escape The Circle',
    'Max Fishing',
    'Mobile Warfare',
    '21 Duel',
    'MaXplosion',
    'Max Slam',
    'Scarab Plinko',
    'Roll Or Hold',
    'Max Blocks',
    'Rolling 21',
  ];
  return sortGames(games, sortOrder);
}

export function sortSoloPlayGames(games: IGameData[]) {
  const sortOrder = ['Scarab Plinko SP', 'Olympus Plinko', 'Max Dice', 'BitBet'];
  return sortGames(games, sortOrder);
}

function sortGames(games: IGameData[], sortOrder: string[]) {
  return games.sort(
    (a, b) => (sortOrder.indexOf(a.techName) + 1 || Infinity) - (sortOrder.indexOf(b.techName) + 1 || Infinity),
  );
}

export const useGamesStore = defineStore('games', () => {
  const list = ref<IGameData[]>([]);

  const pvpGamesListLobby = computed(() => {
    const result = list.value.filter((game) => game.category === EGameCategory.PlayerVsPlayer);
    return sortPvpGamesLobby(result);
  });
  const pvpGamesListDesktop = computed(() => {
    const result = list.value.filter((game) => game.category === EGameCategory.PlayerVsPlayer);
    return sortPvpGamesDesktop(result);
  });
  const pvpGamesListMobile = computed(() => {
    const result = list.value.filter((game) => game.category === EGameCategory.PlayerVsPlayer);
    return sortPvpGamesMobile(result);
  });

  const gameListPvP = computed(() => {
    const result = list.value.filter((game) => game.category === EGameCategory.PlayerVsPlayer);
    return sortPvpGames(result);
  });

  const gameListSoloPlay = computed<IGameData[]>(() => {
    const result = list.value.filter((game) => game.category === EGameCategory.SoloPlay);
    return sortSoloPlayGames(result);
  });

  const gameData = ref<GameWindowData | undefined>();
  const privateRoomId = ref<string | null>(null);
  const gameIsLaunching = ref(false);

  const isUnityGame = computed(() => (gameId: number) => {
    return !!list.value.find((g) => g.id === gameId)?.unityGame;
  });

  const getGameConfigById = computed(() => (gameId: number) => list.value.find((g) => g.id === gameId) as IGameData);

  const SID = ref<string | null>(null);

  function setPrivateRoomId(value: string | null) {
    privateRoomId.value = value;
  }

  async function getGamesList() {
    if (list.value.length) return;

    try {
      const { $authApi } = useTypedNuxtApp();
      const { haserror, games } = await $authApi.get<IGamesListResponse>('/active-games');

      if (haserror) {
        throw new Error('failed to get games list');
      }

      list.value = games
        .filter((game: { active: boolean }) => game.active)
        .map((game: any): IGameData => {
          return {
            id: game.id,
            title: game.displayName,
            techName: game.name,
            launchUrl: game.launchUrl,
            description: game.description,
            bulletDescription: getFrontendBulletDescription(game.bulletDescription),
            logo: game.logo,
            gameType: game.gameType,
            category: game.category,
            labels: game.labels,
            backButtonURL: game.homeUrlId,
            image: game.imageUrl,
            imageCAF: game.imageCAF,
            imageMostPopular: game.imageMostPopular,
            comingSoon: game.comingSoon,
            hidden: game.hidden,
            paramsType: game.isExtended ? 'extended' : 'default',
            buyInConfig: getFrontendBuyInConfig(game.buyInConfig),
            buyInBgUrl: game.buyInBgUrl,
            buyInLogoUrl: game.buyInLogoUrl,
            isNew: game.isNewGame,
            unityGame: game.unityGame,
            sortOrder: game.sortOrder,
          } as IGameData;
        });
    } catch (error) {
      console.error(error);
    }
  }

  function setGameData(data: GameWindowData | undefined) {
    gameData.value = data;
  }

  function showPmTooltipIfNeeded() {
    const lsKey = `pmTooltipOnGameExit-${stores.user.getUserId()}`;
    const needToShow = !localStorage.getItem(lsKey);

    if (needToShow) {
      localStorage.setItem(lsKey, '1');
      useTypedNuxtApp().callHook(EAppCustomHooks.PlayMoneyShowTooltip).then();
    }
  }

  function closeGame() {
    if (!gameData.value) return;

    sendFinishGameSessionRequest({ sid: SID.value, privateRoomId: privateRoomId.value }).then();

    gameData.value = undefined;
    privateRoomId.value = null;

    showPmTooltipIfNeeded();
  }

  async function declineUnityChallenge(data: IDeclineUnityChallengePayload) {
    const { $authApi } = useTypedNuxtApp();

    try {
      return $authApi.post<{ error?: boolean; message: string }>('/declineInvitationToRoom', data);
    } catch (error: any) {
      console.error(error);
      return { error: true, message: error?.data?.message || 'Failed to receive data from an external API' };
    }
  }

  async function declineChallenge(privateRoomId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      return $authApi.post<{ error?: boolean; message: string }>('/declineChallenge', { privateRoomId, sessionKey });
    } catch (error: any) {
      console.error(error);
      return { error: true, message: error?.data?.message || 'Failed to receive data from an external API' };
    }
  }

  async function acceptChallenge(privateRoomId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const { haserror } = await $authApi.post<ICommonAPIResponse>('/acceptChallenge', {
        privateRoomId,
        sessionKey,
      });
      return { error: haserror, message: haserror ? 'Something went wrong' : '' };
    } catch (error: any) {
      console.error(error);
      const message: string = error?.data?.message || 'Failed to receive data from an external API';
      return { error: true, message };
    }
  }

  function setGameIsLaunching(value: boolean) {
    gameIsLaunching.value = value;
  }

  // MARK: CAF
  const rooms = shallowRef<IRoom[]>([]);

  async function getAllUserRooms() {
    const { $authApi } = useTypedNuxtApp();
    const { apiWalletUrl } = useRuntimeConfig().public;

    try {
      const sessionKey = await stores.user.getIdToken();
      const { hasError, data } = await $authApi.post<IGetAllRoomsResponse>(
        '/getAllRooms',
        { sessionKey },
        { baseURL: apiWalletUrl },
      );

      if (hasError || !data) {
        throw new Error('Error fetching CAF rooms');
      }

      rooms.value = data;
    } catch (error: any) {
      console.error(error);
    }
  }

  async function deactivatePrivateRoom(room: IPrivateRoom) {
    const { $authApi } = useTypedNuxtApp();
    const { apiWalletUrl } = useRuntimeConfig().public;

    try {
      const sessionKey = await stores.user.getIdToken();

      if (room.isUnity) {
        return $authApi.post<IGenericAPIResponse>(
          '/closePrivateRoom',
          { gameId: room.gameId, platformId: stores.user.getUserId(), roomId: room.privateRoomId },
          { baseURL: apiWalletUrl },
        );
      } else {
        return $authApi.post<IGenericAPIResponse>('/deactivatePrivateRoom', {
          privateRoomId: room.privateRoomId,
          privateRoomToken: room.privateRoomToken,
          bankId: room.bankId,
          sessionKey,
        });
      }
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error.message || 'Failed to retrieve rooms',
      };
    }
  }

  async function setLoadingClient(userId: string, gameId: number) {
    const { $authApi } = useTypedNuxtApp();
    const { apiWalletUrl } = useRuntimeConfig().public;

    try {
      const sessionKey = await stores.user.getIdToken();
      await $authApi.post(
        '/setPlayerLoadingClient',
        { platformId: userId, gameId, sessionKey },
        { baseURL: apiWalletUrl },
      );
    } catch (error: any) {
      console.error(error);
    }
  }

  function getGameDisplayTitle(techName: string) {
    return list.value.find((g) => g.techName === techName)?.title || techName;
  }

  async function sendFinishGameSessionRequest(params: IFinishGameSessionParams) {
    const { sid, privateRoomId } = params;
    if (!sid) return;

    const { $authApi } = useTypedNuxtApp();
    const { apiWalletUrl } = useRuntimeConfig().public;

    try {
      const sessionKey = await stores.user.getIdToken();
      await $authApi.post<IFinishGameSessionResponse>(
        '/finishGameSession',
        { sessionKey, sid, privateRoomId },
        { baseURL: apiWalletUrl },
      );
    } catch (error) {
      console.error(error);
    }
  }

  function postSIDMessage(domain: string) {
    try {
      SID.value = null;
      window.frames.gameUrlFrame?.postMessage({ SID: 'sid' }, domain);
    } catch (e) {
      console.error('post message sid error...', e);
    }
  }

  function postMessage(domain: string) {
    try {
      window.frames.gameUrlFrame?.postMessage({ message: 'message' }, domain);
    } catch (e) {
      console.error('post message error...', e);
    }
  }

  function handleGamePostMessage(event: MessageEvent) {
    const gameOrigin = new URL(gameData.value.url).origin;
    if (gameOrigin !== event?.origin) return;

    if (event?.data?.type === 'openAddFriendsDialog') {
      stores.modals.open('FriendsPopup', LazyModalManageFriends);
    }

    if (event?.data?.sid && SID.value !== event.data.sid) {
      SID.value = event.data.sid;
      saveGameSession().then();
    }
  }

  async function saveGameSession() {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      await $authApi.post('/addGameSession', { sessionKey, SID: SID.value, privateRoomId: privateRoomId.value });
    } catch (error) {
      console.error('Error saving game session:', error);
    }
  }

  return {
    list,
    gameData,
    privateRoomId,
    setPrivateRoomId,
    getGamesList,
    gameListSoloPlay,
    gameListPvP,
    pvpGamesListLobby,
    pvpGamesListDesktop,
    pvpGamesListMobile,

    setGameData,
    closeGame,
    getGameConfigById,
    isUnityGame,
    gameIsLaunching,

    declineUnityChallenge,
    declineChallenge,
    acceptChallenge,
    setGameIsLaunching,

    rooms,
    getAllUserRooms,
    getGameDisplayTitle,
    deactivatePrivateRoom,
    setLoadingClient,

    postSIDMessage,
    handleGamePostMessage,
    postMessage,
  };
});
